import { memo } from "react";
import { AdjustmentsHorizontalIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useBreakpointContext } from "@/contexts/BreakpointContext";
import { getRoastTermColor } from "@/helpers/helpers";
import BottomSheet, { useBottomSheet } from "@/components/ui/bottomSheet";
import type { ProductAttributeTerm } from "@/types/productAttribute";

interface RoastAttrTermsProps {
  terms: ProductAttributeTerm[];
  selectedTerm: ProductAttributeTerm | null;
  isLoading: boolean;
  onSelect: (term: ProductAttributeTerm | null) => void;
}

const RoastAttrTermsComponent = ({
  terms,
  selectedTerm,
  isLoading,
  onSelect,
}: RoastAttrTermsProps) => {
  const { isMobile, isTablet, isDesktop } = useBreakpointContext();
  const roastAttrSheet = useBottomSheet();

  const handleTermSelect = (
    _: React.MouseEvent<HTMLButtonElement>,
    term: ProductAttributeTerm | null,
  ): void => {
    onSelect(term);
    roastAttrSheet.handleClose();
  };

  if (isLoading) {
    return isDesktop ? <TermsDesktopSkeleton /> : <TermsMobileSkeleton />;
  }

  if (terms.length === 0) return null;

  if (isMobile || isTablet) {
    return (
      <>
        <div className="relative inline-flex items-center gap-1">
          <AdjustmentsHorizontalIcon className="size-6" />
          <button
            type="button"
            className="btn btn-outline btn-sm font-normal"
            onClick={roastAttrSheet.handleOpen}
          >
            <span className="text-neutral">焙度 </span>
            {selectedTerm ? selectedTerm.name : ""}
            <ChevronDownIcon className="size-4" />
          </button>
          {selectedTerm && <ResetButton onReset={() => onSelect(null)} />}
        </div>
        <BottomSheet
          show={roastAttrSheet.isOpen}
          title="依烘焙度篩選"
          onClose={roastAttrSheet.handleClose}
        >
          <Terms terms={terms} selectedTerm={selectedTerm} onSelect={handleTermSelect} />
        </BottomSheet>
      </>
    );
  }

  return (
    <div className="flex flex-col justify-start gap-2">
      <h4 className="brand-text-sm font-bold text-base-content">依烘焙度篩選</h4>
      <div className="flex items-center gap-2">
        <AdjustmentsHorizontalIcon className="size-6 text-base-content" />
        <Terms terms={terms} selectedTerm={selectedTerm} onSelect={(_, term) => onSelect(term)} />
        {selectedTerm && <ResetButton onReset={() => onSelect(null)} />}
      </div>
    </div>
  );
};

const Terms = memo(
  ({
    terms,
    selectedTerm,
    onSelect,
  }: {
    terms: ProductAttributeTerm[];
    selectedTerm: ProductAttributeTerm | null;
    onSelect: (e: React.MouseEvent<HTMLButtonElement>, term: ProductAttributeTerm | null) => void;
  }) => {
    const { isMobile, isTablet } = useBreakpointContext();

    const buttonClass =
      "flex items-center gap-2 font-normal lg:btn lg:btn-outline lg:btn-secondary lg:btn-sm lg:font-normal";

    return (
      <div className="flex flex-col items-start gap-6 lg:flex-row lg:gap-2">
        {terms.map((term) =>
          isMobile || isTablet ? (
            <label key={term.id} className={"flex w-full cursor-pointer items-center gap-2"}>
              <span
                className={clsx("inline-block h-5 w-5 rounded-full", getRoastTermColor(term.slug))}
              />
              <span className="flex-1">{term.name}</span>
              <input
                type="radio"
                name="roastTerm"
                value={term.id}
                className={clsx(
                  "radio-secondary radio radio-sm self-end",
                  term.id !== (selectedTerm && selectedTerm.id) ? "opacity-20" : "",
                )}
                checked={term.id === (selectedTerm && selectedTerm.id)}
                onChange={(e) => onSelect(e, term)}
              />
            </label>
          ) : (
            <button
              key={term.id}
              type="button"
              className={clsx(
                buttonClass,
                term.id === (selectedTerm && selectedTerm.id)
                  ? "font-bold text-primary lg:bg-secondary lg:!text-white"
                  : "",
              )}
              onClick={(e) => onSelect(e, term)}
            >
              <span
                className={clsx("inline-block h-5 w-5 rounded-full", getRoastTermColor(term.slug))}
              />
              {term.name}
            </button>
          ),
        )}
      </div>
    );
  },
);

const ResetButton = ({ onReset }: { onReset: () => void }) => (
  <button
    type="button"
    className="btn btn-outline btn-neutral btn-sm flex items-center gap-1 font-normal"
    onClick={onReset}
  >
    <XMarkIcon className="size-4" />
    清除
  </button>
);

const TermsDesktopSkeleton = () => (
  <div>
    <div className="mb-2 h-5 w-24 animate-pulse bg-base-300" />
    <div className="flex gap-2">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="h-8 w-20 animate-pulse bg-base-300" />
      ))}
    </div>
  </div>
);

const TermsMobileSkeleton = () => <div className="h-8 w-32 animate-pulse bg-base-300" />;

export default RoastAttrTermsComponent;
